import type { JSX } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Box, useOnClickOutside, useThemeColor } from '@wistia/vhs';
import { isNonEmptyString } from '~/utilities/type-guards';
import { SpeakerAvatar } from '~/singlePageApp/screens/LiveStreamScreen/shared/components/SpeakerAvatar';
import { SpeakerSocialMediaLinks } from '~/singlePageApp/screens/LiveStreamScreen/AudienceRegistration/SpeakerSocialMediaLinks';
import type { Speaker as MediaSpeaker, LiveStreamEventSpeaker } from '~/graphql';
import { trackStructEvent } from '~/utilities/snowplow';
import {
  Bio,
  BioWrapper,
  FullBioToggle,
  SpeakerBioItem,
  SpeakerBioItemContainer,
  SpeakerTileText,
} from './LayoutComponents';
import type { SpeakerBioGridItemStyleStrategy } from './components/SpeakerBioStyles';
import { SpeakerBioStyles } from './components/SpeakerBioStyles';
import { StyleVariableFactories } from './theming/StyleVariableFactories';

export type SpeakerType = LiveStreamEventSpeaker | MediaSpeaker;

type Props = {
  speaker: SpeakerType;
  styleStrategy?: SpeakerBioGridItemStyleStrategy;
};

export const Speaker = ({ speaker, styleStrategy }: Props): JSX.Element => {
  const [showBio, setShowBio] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasOverflowText, setHasOverflowText] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bioRef = useRef<HTMLDivElement | null>(null);
  const colorTheme = useThemeColor();

  const styleVariableFactories = StyleVariableFactories.create(colorTheme);

  useOnClickOutside(containerRef, () => {
    setShowBio(false);
  });

  const { name, title, bio, imageUrl, socialMediaUrls } = speaker.publicProfile;
  const badgeText = speaker.role === 'host' ? 'Host' : undefined;
  const hasBio = isNonEmptyString(bio);

  const isEventRoom = styleStrategy === 'event-room';

  const handleBioClick = () => {
    if (hasOverflowText && hasBio) {
      setShowBio(!showBio);

      trackStructEvent({
        category: 'link',
        action: 'click',
        label: 'speaker-bio-interactions',
        property: 'speakers',
        value: 'show-more',
      });
    }
  };

  const showBoxShadow = () => setIsHovering(true);
  const hideBoxShadow = () => setIsHovering(false);

  useEffect(() => {
    if (bioRef.current !== null) {
      const isOverflowing = bioRef.current.offsetHeight < bioRef.current.scrollHeight;

      if (isOverflowing) {
        setHasOverflowText(true);
        return;
      }

      setHasOverflowText(false);
    }
  }, [bio]); // when editing bio we want to recheck if there is overflow text

  return (
    <>
      <SpeakerBioStyles
        styleStrategy={styleStrategy}
        hasBio={hasBio}
        showBio={showBio}
      />
      <SpeakerBioItem
        hasBio={hasBio}
        isHovering={isHovering}
        hasOverflowText={hasOverflowText}
        isEventRoom={isEventRoom}
        onMouseEnter={showBoxShadow}
        onMouseLeave={hideBoxShadow}
      >
        <SpeakerBioItemContainer
          isEventRoom={isEventRoom}
          hasBio={hasBio}
          showBio={showBio}
          ref={containerRef}
        >
          <Box
            direction="column"
            justifyContent="center"
            gap="1rem"
            style={{ width: '100%', maxWidth: '100%' }}
          >
            <Box
              alignItems="center"
              direction="column"
              gap="0.5rem"
              style={{ width: '100%', maxWidth: '100%' }}
            >
              <SpeakerAvatar
                imageUrl={imageUrl}
                shouldShowIcon={true}
                showBorder={false}
                heightAndWidth={72}
                badgeText={badgeText}
                badgeBackgroundColor={styleVariableFactories.speakerAvatarBadgeBackgroundColor(
                  styleStrategy,
                )}
                badgeTextColor={styleVariableFactories.speakerAvatarBadgeTextColor(styleStrategy)}
                avatarBackgroundColor={styleVariableFactories.speakerAvatarBackgroundColor(
                  styleStrategy,
                )}
                avatarIconColor={styleVariableFactories.speakerAvatarIconColor(styleStrategy)}
              />
              <Box
                direction="column"
                alignItems="center"
                gap="0.25rem"
              >
                <SpeakerTileText
                  variant="headline2"
                  isEventRoom={isEventRoom}
                >
                  {name}
                </SpeakerTileText>
                <SpeakerTileText
                  variant="caption"
                  className="title"
                  isEventRoom={isEventRoom}
                >
                  {title}
                </SpeakerTileText>
              </Box>
            </Box>

            <BioWrapper
              className="bio"
              hasOverflowText={hasOverflowText}
              ref={bioRef}
            >
              <Bio
                variant="body2"
                isEventRoom={isEventRoom}
              >
                {/* eslint-disable-next-line react/no-danger */}
                <span dangerouslySetInnerHTML={{ __html: bio ?? '' }} />
              </Bio>
            </BioWrapper>

            {hasOverflowText ? (
              <FullBioToggle
                isEventRoom={isEventRoom}
                onClick={handleBioClick}
                variant="body2"
              >
                Show {showBio ? 'less' : 'more'}...
              </FullBioToggle>
            ) : null}
          </Box>

          <SpeakerSocialMediaLinks
            links={socialMediaUrls}
            styleStrategy={styleStrategy}
          />
        </SpeakerBioItemContainer>
      </SpeakerBioItem>
    </>
  );
};
